import { createSelector } from 'reselect';

import { GRAPH_FIELD_TYPE } from '../../domain/dataKiGraph';
import {
  DataModelTransitionGraph,
  DataModelTransitionGraphFieldItem,
  DataModelTransitionGraphParams,
} from '../../domain/dataModelTransitionGraph';
import { buildGraphListForNormalizedFields } from '../../domain/dataModelTransitionGraphFields';
import { getGraphCheckedRowIdsFromSetting } from '../../domain/modelTransition/mainField';
import { LoadingState } from '../../domain/schemas';

import { RootState } from '../../store';
import { ChangeModelTransitionReportsCurrentHallsAction } from '../ui/modelTransitionReportsSetting';
import { dataModelTransitionKiOptionsSelector } from './dataModelTransition';

// Action Types

const FETCH_DATA_MODEL_TRANSITION_GRAPH =
  'FETCH_DATA_MODEL_TRANSITION_GRAPH' as const;
const FETCH_DATA_MODEL_TRANSITION_GRAPH_REQUEST =
  'FETCH_DATA_MODEL_TRANSITION_GRAPH_REQUEST' as const;
const FETCH_DATA_MODEL_TRANSITION_GRAPH_SUCCESS =
  'FETCH_DATA_MODEL_TRANSITION_GRAPH_SUCCESS' as const;
const SEARCH_DATA_MODEL_TRANSITION_GRAPH =
  'SEARCH_DATA_MODEL_TRANSITION_GRAPH' as const;
const CHANGE_DATA_MODEL_TRANSITION_GRAPH_KI =
  'CHANGE_DATA_MODEL_TRANSITION_GRAPH_KI' as const;
const SET_DATA_MODEL_TRANSITION_GRAPH_FIELDS =
  'SET_DATA_MODEL_TRANSITION_GRAPH_FIELDS' as const;
const SET_DATA_MODEL_TRANSITION_GRAPH_GRAPH_LIST =
  'SET_DATA_MODEL_TRANSITION_GRAPH_GRAPH_LIST' as const;
const RENEW_DATA_MODEL_TRANSITION_GRAPH =
  'RENEW_DATA_MODEL_TRANSITION_GRAPH' as const;

export const DataModelTransitionGraphActionTypes = {
  FETCH_DATA_MODEL_TRANSITION_GRAPH,
  FETCH_DATA_MODEL_TRANSITION_GRAPH_REQUEST,
  FETCH_DATA_MODEL_TRANSITION_GRAPH_SUCCESS,
  SEARCH_DATA_MODEL_TRANSITION_GRAPH,
  CHANGE_DATA_MODEL_TRANSITION_GRAPH_KI,
  SET_DATA_MODEL_TRANSITION_GRAPH_FIELDS,
  SET_DATA_MODEL_TRANSITION_GRAPH_GRAPH_LIST,
  RENEW_DATA_MODEL_TRANSITION_GRAPH,
};
// Action Creators

/**
 * 推移グラフ（機種比較）のデータ取得を開始する。
 * 表示項目は params.fields（なければ field）から決まる。
 */
function fetchDataModelTransitionGraphAction(
  params: DataModelTransitionGraphParams
) {
  return {
    type: FETCH_DATA_MODEL_TRANSITION_GRAPH,
    payload: { params },
  };
}

/**
 * 推移グラフ（機種比較）データ取得前に実行する
 */
function fetchDataModelTransitionGraphRequestAction() {
  return {
    type: FETCH_DATA_MODEL_TRANSITION_GRAPH_REQUEST,
  };
}

/**
 * 推移グラフ（機種比較）データ取得完了時データを登録する
 * @param graph 取得したグラフデータ
 */
function fetchDataModelTransitionGraphSuccessAction(
  graph: DataModelTransitionGraph
) {
  return {
    type: FETCH_DATA_MODEL_TRANSITION_GRAPH_SUCCESS,
    payload: { graph },
  };
}

/**
 * チェックした行の推移グラフを表示する
 * @param checkedRowIds チェックした行 ID 一覧
 */
function searchDataModelTransitionGraphAction(checkedRowIds: string[]) {
  return {
    type: SEARCH_DATA_MODEL_TRANSITION_GRAPH,
    payload: { checkedRowIds },
  };
}

/**
 * 指定した機種コードでグラフを絞り込む（機種選択フォーム用）
 * @param kiList 絞り込む機種コード一覧
 */
function changeDataModelTransitionGraphKiAction(kiList: string[]) {
  return {
    type: CHANGE_DATA_MODEL_TRANSITION_GRAPH_KI,
    payload: { kiList },
  };
}

/**
 * 表示するグラフ項目を配列で一括指定して再取得する。
 */
function setDataModelTransitionGraphFieldsAction(fields: string[]) {
  return {
    type: SET_DATA_MODEL_TRANSITION_GRAPH_FIELDS,
    payload: { fields },
  };
}

/**
 * 取得済み graphList を fields に合わせて更新する（× 削除・2本目を「未選択」にしたとき）。
 * SET_DATA_MODEL_TRANSITION_GRAPH_FIELDS と違い Saga は走らず、API 再取得・ローダーもなし。
 */
function setDataModelTransitionGraphGraphListAction(fields: string[]) {
  return {
    type: SET_DATA_MODEL_TRANSITION_GRAPH_GRAPH_LIST,
    payload: { fields },
  };
}

/**
 * 推移グラフ（機種比較）のデータをクリアする
 */
function renewDataModelTransitionGraphAction() {
  return {
    type: RENEW_DATA_MODEL_TRANSITION_GRAPH,
  };
}

export const DataModelTransitionGraphActionCreators = {
  fetchDataModelTransitionGraphAction,
  fetchDataModelTransitionGraphRequestAction,
  fetchDataModelTransitionGraphSuccessAction,
  searchDataModelTransitionGraphAction,
  changeDataModelTransitionGraphKiAction,
  setDataModelTransitionGraphFieldsAction,
  setDataModelTransitionGraphGraphListAction,
  renewDataModelTransitionGraphAction,
};

// Actions

export type FetchDataModelTransitionGraphAction = ReturnType<
  typeof fetchDataModelTransitionGraphAction
>;
export type SearchDataModelTransitionGraphAction = ReturnType<
  typeof searchDataModelTransitionGraphAction
>;
export type SetDataModelTransitionGraphFieldsAction = ReturnType<
  typeof setDataModelTransitionGraphFieldsAction
>;
export type SetDataModelTransitionGraphGraphListAction = ReturnType<
  typeof setDataModelTransitionGraphGraphListAction
>;

type DataModelTransitionGraphAction =
  | FetchDataModelTransitionGraphAction
  | SearchDataModelTransitionGraphAction
  | SetDataModelTransitionGraphFieldsAction
  | SetDataModelTransitionGraphGraphListAction
  | ChangeModelTransitionReportsCurrentHallsAction
  | ReturnType<typeof changeDataModelTransitionGraphKiAction>
  | ReturnType<typeof fetchDataModelTransitionGraphRequestAction>
  | ReturnType<typeof fetchDataModelTransitionGraphSuccessAction>
  | ReturnType<typeof renewDataModelTransitionGraphAction>;

// State

type DataModelTransitionGraphState = {
  /**
   * 選択中の機種（機種選択フォームで絞り込んだ機種）
   */
  selectKi: string[];
  /**
   * グラフデータ
   */
  dataModelTransitionGraph: DataModelTransitionGraph | undefined;
  /**
   * ローディング状態
   */
  loadingState: LoadingState;
};

const initialState: DataModelTransitionGraphState = {
  selectKi: [],
  dataModelTransitionGraph: undefined,
  loadingState: 'prepare',
};

// Selector

/**
 * グラフデータを取得する
 * @returns グラフデータ
 */
export const dataModelTransitionGraphSelector = (state: RootState) =>
  state.dataModelTransitionGraph.dataModelTransitionGraph;

/**
 * グラフ項目ごとのデータ一覧を取得する
 */
export const dataModelTransitionGraphGraphListSelector = createSelector(
  dataModelTransitionGraphSelector,
  (graph): DataModelTransitionGraphFieldItem[] => graph?.graphList ?? []
);

/**
 * 推移グラフ（機種比較）のデータが存在するか。
 * graphList.length > 0 と同値だが、重い配列を購読せず boolean だけを購読したい
 * 呼び出し側（存在判定・開閉アニメーション判定）向けに用意する。
 */
export const dataModelTransitionGraphIsExistSelector = createSelector(
  dataModelTransitionGraphSelector,
  (graph): boolean => (graph?.graphList.length ?? 0) > 0
);

/**
 * Redux の setting + graphList から、再取得・お気に入り保存用の params を組み立てる。
 * toDataModelTransitionGraphSetting で除いた field / fields を graphList から復元する。
 */
export const dataModelTransitionGraphSettingSelector = createSelector(
  dataModelTransitionGraphSelector,
  (graph): DataModelTransitionGraphParams | undefined => {
    if (!graph) {
      return undefined;
    }
    const graphFields = graph.graphList.map((item) => item.field);
    const firstField = graphFields.at(0) ?? GRAPH_FIELD_TYPE.OUT;
    return {
      ...graph.setting,
      field: firstField,
      ...(graphFields.length > 0 ? { fields: graphFields } : {}),
    };
  }
);

/**
 * グラフデータのロード状態を取得する
 * @returns グラフデータ
 */
export const dataModelTransitionGraphSettingLoadingStateSelector = (
  state: RootState
) => state.dataModelTransitionGraph.loadingState;

/**
 * 絞り込んでいる機種一覧を取得する
 * @returns 機種一覧
 */
export const dataModelTransitionGraphSelectedKiListSelector = (
  state: RootState
) => state.dataModelTransitionGraph.selectKi;

type DataModelTransitionGraphKiOption = {
  code: string;
  name: string;
};

/**
 * グラフ表示時に選択した機種のうち、現在のメインテーブルに存在する機種一覧
 */
export const dataModelTransitionGraphCheckedKiOptionsSelector = createSelector(
  dataModelTransitionGraphSelector,
  dataModelTransitionKiOptionsSelector,
  (graph, rowOptions): DataModelTransitionGraphKiOption[] => {
    const checkedRowIds = getGraphCheckedRowIdsFromSetting(graph?.setting);
    return checkedRowIds.flatMap((rowId) => {
      const name = rowOptions[rowId];
      if (name === undefined) {
        return [];
      }
      return [{ code: rowId, name }];
    });
  }
);

/**
 * グラフ機種セレクタの表示・描画用。selectKi のうちメインテーブルに存在する機種のみ
 */
export const dataModelTransitionGraphEffectiveSelectedKiListSelector =
  createSelector(
    dataModelTransitionGraphSelectedKiListSelector,
    dataModelTransitionGraphCheckedKiOptionsSelector,
    (selectedKiList, checkedKiOptions): string[] => {
      const availableCodes = new Set(
        checkedKiOptions.map((option) => option.code)
      );
      return selectedKiList.filter((ki) => availableCodes.has(ki));
    }
  );

// Reducer
export function dataModelTransitionGraphReducer(
  state = initialState,
  action: DataModelTransitionGraphAction
): DataModelTransitionGraphState {
  switch (action.type) {
    case FETCH_DATA_MODEL_TRANSITION_GRAPH_REQUEST:
      return {
        ...state,
        loadingState: 'loading',
      };
    case FETCH_DATA_MODEL_TRANSITION_GRAPH_SUCCESS:
      return {
        ...state,
        loadingState: 'loaded',
        dataModelTransitionGraph: action.payload.graph,
      };
    case SEARCH_DATA_MODEL_TRANSITION_GRAPH:
      return {
        ...state,
        selectKi: action.payload.checkedRowIds,
      };
    case CHANGE_DATA_MODEL_TRANSITION_GRAPH_KI:
      return {
        ...state,
        selectKi: action.payload.kiList,
      };
    case SET_DATA_MODEL_TRANSITION_GRAPH_GRAPH_LIST: {
      const graph = state.dataModelTransitionGraph;
      if (!graph) {
        return state;
      }
      const nextGraphList = buildGraphListForNormalizedFields(
        graph.graphList,
        action.payload.fields
      );
      return {
        ...state,
        dataModelTransitionGraph: {
          ...graph,
          graphList: nextGraphList,
        },
      };
    }
    case RENEW_DATA_MODEL_TRANSITION_GRAPH:
      return initialState;
    default:
      return state;
  }
}
